import {
  addHours,
  differenceInMinutes,
  endOfDay,
  isValid,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
} from "date-fns";
import { DateTimeSettingsEnum } from "../../enum/app-settings/AppSettingsEnum";
import { KPISettingsIDs } from "../../enum/FlightsEnum";
import {
  MultiAirportDateRangeConfig,
  MultiAirportDateRangePayload,
} from "../../models/airports/AirportConfig";
import {
  IApiResourceResponse,
  IKpiSettingsAPI,
  IKpiSettingsResponse,
  ITimeSettings,
} from "../../models/app-settings/AppSettingsModels";
import { convertTimeStringToHourMinute } from "../app-settings/AppSettingsHelper";
import {
  convertToRollingHours,
  formatDateForApiPayload,
  getDateTimeForTimeZone,
} from "../common/DateHelperFunctions";

const MAX_RANGE_MINUTES = 24 * 60;
const TIME_PATTERN = /^-?\d{1,3}:\d{1,2}$/;

interface ParsedTime {
  hours: number;
  minutes: number;
}

const getAirportCode = (airport: MultiAirportDateRangeConfig): string =>
  (airport.iataCode || airport.code || airport.id || "").trim();

const parseTime = (time: string, allowNegative: boolean): ParsedTime => {
  if (typeof time !== "string" || !TIME_PATTERN.test(time.trim())) {
    throw new Error(`Invalid time value: ${time || "empty"}`);
  }

  const parsedTime = convertTimeStringToHourMinute(time.trim());
  if (
    !Number.isInteger(parsedTime.hours) ||
    !Number.isInteger(parsedTime.minutes) ||
    parsedTime.minutes < 0 ||
    parsedTime.minutes > 59 ||
    (!allowNegative &&
      (parsedTime.hours < 0 || Object.is(parsedTime.hours, -0))) ||
    (!allowNegative && parsedTime.hours > 23)
  ) {
    throw new Error(`Invalid time value: ${time}`);
  }

  return parsedTime;
};

const validateTimeZone = (timeZoneId: string, currentTime: Date) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timeZoneId }).format(
      currentTime
    );
  } catch (_error) {
    throw new Error(`Invalid airport time zone: ${timeZoneId}`);
  }
};

const validateRange = (startDate: Date, endDate: Date) => {
  const rangeMinutes = differenceInMinutes(endDate, startDate);
  if (
    !isValid(startDate) ||
    !isValid(endDate) ||
    rangeMinutes <= 0 ||
    rangeMinutes > MAX_RANGE_MINUTES
  ) {
    throw new Error(
      "The calculated KPI date range must be greater than 0 and no more than 24 hours"
    );
  }
};

export const getKpiSetting = (
  kpiSettingsResponse: IKpiSettingsResponse,
  kpiSettingsId: KPISettingsIDs
): IKpiSettingsAPI => {
  const kpiSetting = kpiSettingsResponse?.kpiSettings?.find(
    (setting) => setting.name === kpiSettingsId
  );

  if (!kpiSetting) {
    throw new Error(`${kpiSettingsId} KPI settings are unavailable`);
  }

  return kpiSetting;
};

export const getOtpKpiSetting = (
  kpiSettingsResponse: IKpiSettingsResponse
): IKpiSettingsAPI =>
  getKpiSetting(kpiSettingsResponse, KPISettingsIDs.ON_TIME_PERFORMANCE);

const getSelectedDateTimeSetting = (
  dateTimeSettingsResponse: IApiResourceResponse | undefined
): ITimeSettings => {
  if (!dateTimeSettingsResponse) {
    throw new Error(
      "Date-time settings are required to calculate the selected range"
    );
  }

  const { selectedConfig, timeSettings } = dateTimeSettingsResponse;
  if (
    selectedConfig !== DateTimeSettingsEnum.RollingHrs &&
    selectedConfig !== DateTimeSettingsEnum.TimeDuration
  ) {
    throw new Error(
      `Unsupported selected date-time configuration: ${selectedConfig}`
    );
  }

  const selectedSetting = timeSettings?.find(
    (setting) => setting.name === selectedConfig
  );
  if (!selectedSetting) {
    throw new Error(
      `Selected date-time setting is unavailable: ${selectedConfig}`
    );
  }

  return selectedSetting;
};

const calculateRollingRange = (
  currentAirportTime: Date,
  from: string,
  to: string
) => {
  const fromTime = parseTime(from, true);
  const toTime = parseTime(to, true);
  const normalizedCurrentTime = setMilliseconds(
    setSeconds(currentAirportTime, 0),
    0
  );
  const startDate = addHours(
    normalizedCurrentTime,
    convertToRollingHours(fromTime.hours, fromTime.minutes)
  );
  const endDate = addHours(
    normalizedCurrentTime,
    convertToRollingHours(toTime.hours, toTime.minutes)
  );

  validateRange(startDate, endDate);
  return { startDate, endDate };
};

const calculateFixedRange = (
  currentAirportTime: Date,
  from: string,
  to: string
) => {
  const fromTime = parseTime(from, false);
  const toTime = parseTime(to, false);
  const startDate = setMinutes(
    setHours(startOfDay(currentAirportTime), fromTime.hours),
    fromTime.minutes
  );
  const endDate = setMinutes(
    setHours(endOfDay(currentAirportTime), toTime.hours),
    toTime.minutes
  );

  validateRange(startDate, endDate);
  return { startDate, endDate };
};

const getAirportLocalTime = (
  airport: MultiAirportDateRangeConfig,
  currentTime: Date
) => {
  const airportCode = getAirportCode(airport);
  if (!airportCode) {
    throw new Error("Airport code is unavailable");
  }
  if (!airport.timeZoneId) {
    throw new Error(`Airport time zone is unavailable for ${airportCode}`);
  }
  if (!isValid(currentTime)) {
    throw new Error("Current system time is invalid");
  }

  validateTimeZone(airport.timeZoneId, currentTime);
  return {
    airportCode,
    currentAirportTime: getDateTimeForTimeZone(currentTime, airport.timeZoneId),
  };
};

const calculateSelectedDateTimeRange = (
  currentAirportTime: Date,
  dateTimeSettingsResponse: IApiResourceResponse | undefined
) => {
  const selectedSetting = getSelectedDateTimeSetting(dateTimeSettingsResponse);
  return dateTimeSettingsResponse?.selectedConfig ===
    DateTimeSettingsEnum.RollingHrs
    ? calculateRollingRange(
        currentAirportTime,
        selectedSetting.from,
        selectedSetting.to
      )
    : calculateFixedRange(
        currentAirportTime,
        selectedSetting.from,
        selectedSetting.to
      );
};

const getDateRangePayload = (
  airportCode: string,
  range: { startDate: Date; endDate: Date }
): MultiAirportDateRangePayload => ({
  airportCode,
  customerId: null,
  start: formatDateForApiPayload(range.startDate),
  end: formatDateForApiPayload(range.endDate),
});

export const calculateAirportDateTimeRange = (
  airport: MultiAirportDateRangeConfig,
  currentTime: Date,
  dateTimeSettingsResponse: IApiResourceResponse
): MultiAirportDateRangePayload => {
  const { airportCode, currentAirportTime } = getAirportLocalTime(
    airport,
    currentTime
  );
  return getDateRangePayload(
    airportCode,
    calculateSelectedDateTimeRange(currentAirportTime, dateTimeSettingsResponse)
  );
};

export const calculateAirportKpiDateRange = (
  airport: MultiAirportDateRangeConfig,
  currentTime: Date,
  kpiSettingsResponse: IKpiSettingsResponse,
  dateTimeSettingsResponse?: IApiResourceResponse,
  kpiSettingsId = KPISettingsIDs.ON_TIME_PERFORMANCE
): MultiAirportDateRangePayload => {
  const { airportCode, currentAirportTime } = getAirportLocalTime(
    airport,
    currentTime
  );
  const kpiSetting = getKpiSetting(kpiSettingsResponse, kpiSettingsId);

  let range;
  if (kpiSetting.isRollingActive) {
    range = calculateRollingRange(
      currentAirportTime,
      kpiSetting.from,
      kpiSetting.to
    );
  } else {
    range = calculateSelectedDateTimeRange(
      currentAirportTime,
      dateTimeSettingsResponse
    );
  }

  return getDateRangePayload(airportCode, range);
};
